import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, X as XIcon } from "lucide-react";
import { api } from "@/lib/api";
import { MOSTRAR_SIGNATARIOS_TELECOM } from "@/lib/config";

// ============================================================================
// Tipos compartidos del subformulario de NOM/DT (certificado nuevo y
// reexpedición por pruebas complementarias).
// ============================================================================

export interface Norma {
  id: string;
  codigo: string;
  titulo: string;
  tipo: string;
}

interface Laboratorio {
  id: string;
  razonSocial: string;
  nombreCorto: string;
}

interface Signatario {
  id: string;
  nombreCompleto: string;
  correo: string;
}

/** Una norma capturada en el formulario, con su laboratorio, signatarios y vigencia. */
export interface NormaForm {
  /** id temporal de UI; al enviar al backend se descarta. */
  uiId: string;
  normaId: string;
  normaCodigo: string;
  /** NOM-221-SCFI-2017-P1 → modo IMEIs (sin laboratorio). */
  esNom221P1: boolean;
  laboratorioId: string;
  laboratorioNombre: string;
  numReporte: string;
  modelosEvaluados: string;
  fechaEmision: string;
  diasVigencia: number;
  numImeis: number | null;
  signatariosIds: string[];
}

const NOM_221_P1 = "NOM-221-SCFI-2017-P1";

/** Construye una fila de norma en blanco para "Agregar norma". */
export function nuevaNormaForm(opts?: { modelosEvaluados?: string }): NormaForm {
  return {
    uiId: cryptoRandomId(),
    normaId: "",
    normaCodigo: "",
    esNom221P1: false,
    laboratorioId: "",
    laboratorioNombre: "",
    numReporte: "",
    modelosEvaluados: opts?.modelosEvaluados ?? "",
    fechaEmision: new Date().toISOString().slice(0, 10),
    diasVigencia: 120,
    numImeis: null,
    signatariosIds: [],
  };
}

/** Mapea una NormaForm al payload que espera el backend (NormaSolicitud). */
export function normaFormToPayload(n: NormaForm) {
  return {
    normaId: n.normaId,
    laboratorioId: n.esNom221P1 ? null : n.laboratorioId || null,
    numReporte: n.numReporte.trim() || null,
    // RF-003: los modelos evaluados conservan su escritura original.
    modelosEvaluados: n.modelosEvaluados.trim() || null,
    fechaEmision: n.fechaEmision || null,
    diasVigencia: n.diasVigencia,
    numImeis: n.esNom221P1 ? n.numImeis : null,
    signatariosIds: n.esNom221P1 ? [] : n.signatariosIds,
  };
}

// ============================================================================
// Modal
// ============================================================================

export function NormaModal({
  initial,
  normas,
  yaUsadas,
  onCancel,
  onSave,
}: {
  initial: NormaForm;
  normas: Norma[];
  yaUsadas: string[];
  onCancel: () => void;
  onSave: (n: NormaForm) => void;
}) {
  const [n, setN] = useState<NormaForm>(initial);
  const [err, setErr] = useState<string | null>(null);

  const normasDisp = useMemo(
    () => normas.filter((x) => !yaUsadas.includes(x.id) || x.id === initial.normaId),
    [normas, yaUsadas, initial.normaId],
  );

  const qLabs = useQuery({
    queryKey: ["telecom-labs", n.normaId],
    queryFn: async () => (await api.get<Laboratorio[]>(`/api/sector/telecom/laboratorios?norma=${n.normaId}`)).data,
    enabled: !!n.normaId && !n.esNom221P1,
  });
  const qSig = useQuery({
    queryKey: ["telecom-sig", n.laboratorioId, n.normaId],
    queryFn: async () => (await api.get<Signatario[]>(`/api/sector/telecom/signatarios?laboratorio=${n.laboratorioId}&norma=${n.normaId}`)).data,
    enabled: !!n.laboratorioId && !!n.normaId && !n.esNom221P1,
  });

  // RF-01: con la lista de signatarios oculta, la operación no los elige a
  // mano; se asocian TODOS los del laboratorio+norma automáticamente (la
  // relación y el certificado no cambian, solo desaparece la selección de la
  // UI). En modo auditoría (flag activo) se conserva la selección manual.
  useEffect(() => {
    if (MOSTRAR_SIGNATARIOS_TELECOM || n.esNom221P1) return;
    const ids = qSig.data?.map((s) => s.id) ?? [];
    setN((prev) => {
      const iguales =
        prev.signatariosIds.length === ids.length &&
        ids.every((id) => prev.signatariosIds.includes(id));
      return iguales ? prev : { ...prev, signatariosIds: ids };
    });
  }, [qSig.data, n.esNom221P1]);

  const onChangeNorma = (id: string) => {
    const sel = normas.find((x) => x.id === id);
    setN({
      ...n,
      normaId: id,
      normaCodigo: sel?.codigo ?? "",
      esNom221P1: sel?.codigo === NOM_221_P1,
      laboratorioId: "", laboratorioNombre: "", signatariosIds: [],
    });
  };

  const guardar = () => {
    if (!n.normaId) return setErr("Selecciona la norma.");
    if (n.esNom221P1) {
      if (!n.numImeis || n.numImeis <= 0) return setErr("Captura la cantidad de IMEIs.");
    } else {
      if (!n.laboratorioId) return setErr("Selecciona el laboratorio.");
      if (!n.numReporte.trim()) return setErr("Captura el número de reporte.");
      if (n.signatariosIds.length === 0) {
        // Con la lista oculta el usuario no puede "seleccionar"; el bloqueo
        // solo puede deberse a que el laboratorio no tiene personal autorizado.
        return setErr(
          MOSTRAR_SIGNATARIOS_TELECOM
            ? "Selecciona al menos un signatario."
            : "El laboratorio seleccionado no tiene personal autorizado para esta norma. Selecciona otro laboratorio.",
        );
      }
      if (!n.modelosEvaluados.trim()) return setErr("Captura los modelos evaluados.");
      if (!n.fechaEmision) return setErr("Captura la fecha de emisión.");
    }
    onSave(n);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg bg-surface shadow-overlay">
        <div className="flex shrink-0 items-center justify-between border-b px-5 py-3">
          <h3 className="text-base font-semibold">{initial.normaId ? "Editar norma" : "Agregar norma"}</h3>
          <button onClick={onCancel} className="rounded p-1 text-fg-subtle hover:bg-surface-2"><XIcon className="h-4 w-4" /></button>
        </div>
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-5">
          <Field label="Norma *">
            <select value={n.normaId} onChange={(e) => onChangeNorma(e.target.value)} className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg">
              <option value="">— Selecciona —</option>
              {normasDisp.map((x) => (<option key={x.id} value={x.id}>{x.codigo} — {x.titulo}</option>))}
            </select>
          </Field>

          {n.esNom221P1 ? (
            <Field label="Cantidad de números de IMEI *">
              <input type="number" min={1} max={999999999} value={n.numImeis ?? ""}
                     onChange={(e) => setN({ ...n, numImeis: e.target.value ? Number(e.target.value) : null })}
                     className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg" />
            </Field>
          ) : (
            n.normaId && (
              <>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Laboratorio *">
                    <select value={n.laboratorioId} onChange={(e) => {
                      const sel = qLabs.data?.find((l) => l.id === e.target.value);
                      setN({ ...n, laboratorioId: e.target.value, laboratorioNombre: sel?.razonSocial ?? "", signatariosIds: [] });
                    }} className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg">
                      <option value="">— Selecciona —</option>
                      {qLabs.data?.map((l) => (<option key={l.id} value={l.id}>{l.razonSocial}</option>))}
                    </select>
                  </Field>
                  <Field label="No. de reporte *">
                    <input value={n.numReporte} onChange={(e) => setN({ ...n, numReporte: e.target.value.toUpperCase() })}
                           className="w-full rounded border px-2 py-1.5 text-sm uppercase" />
                  </Field>
                </div>

                {/* RF-01: la lista de signatarios (ingenieros/asociados del
                    laboratorio) queda oculta en la operación diaria. Reversible
                    con VITE_TELECOM_MOSTRAR_SIGNATARIOS=true (ver lib/config). */}
                {MOSTRAR_SIGNATARIOS_TELECOM && n.laboratorioId && (
                  <Field label="Signatarios autorizados *">
                    <div className="rounded border bg-surface p-2">
                      {qSig.isLoading ? (
                        <div className="text-xs text-fg-subtle">Cargando…</div>
                      ) : qSig.data?.length === 0 ? (
                        <div className="text-xs text-estado-warn">El laboratorio no tiene signatarios autorizados para esta norma.</div>
                      ) : (
                        qSig.data?.map((s) => (
                          <label key={s.id} className="flex items-center gap-2 rounded px-1 py-0.5 text-xs hover:bg-surface-2">
                            <input type="checkbox" checked={n.signatariosIds.includes(s.id)} onChange={() => {
                              const has = n.signatariosIds.includes(s.id);
                              setN({ ...n, signatariosIds: has ? n.signatariosIds.filter((x) => x !== s.id) : [...n.signatariosIds, s.id] });
                            }} />
                            <span>{s.nombreCompleto}</span>
                          </label>
                        ))
                      )}
                    </div>
                  </Field>
                )}

                <Field label="Modelos evaluados *">
                  <input value={n.modelosEvaluados} onChange={(e) => setN({ ...n, modelosEvaluados: e.target.value })}
                         className="w-full rounded border px-2 py-1.5 text-sm" />
                </Field>

                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Fecha de emisión *">
                    <input type="date" max={new Date().toISOString().slice(0, 10)} value={n.fechaEmision}
                           onChange={(e) => setN({ ...n, fechaEmision: e.target.value })}
                           className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg" />
                  </Field>
                  <Field label="Vigencia (días hábiles) *">
                    <input type="number" min={0} value={n.diasVigencia} onChange={(e) => setN({ ...n, diasVigencia: Number(e.target.value) || 0 })}
                           className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg" />
                  </Field>
                </div>
              </>
            )
          )}

          {err && (
            <div className="flex items-start gap-2 rounded bg-estado-err-bg p-2 text-xs text-estado-err">
              <AlertCircle className="mt-0.5 h-4 w-4" />
              <span>{err}</span>
            </div>
          )}
        </div>
        <div className="flex shrink-0 justify-end gap-2 border-t bg-surface-2 px-5 py-3">
          <button onClick={onCancel} className="rounded border px-3 py-1.5 text-sm text-fg-muted hover:bg-surface-2">Cancelar</button>
          <button onClick={guardar} className="rounded bg-primary px-3 py-1.5 text-sm text-white hover:opacity-90">Guardar</button>
        </div>
      </div>
    </div>
  );
}

// Field local del modal (mismo estilo que el Field de las páginas de alta).
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-fg-subtle">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function cryptoRandomId(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}
